import type { ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { Navigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

/**
 * Where the user was heading before they were sent to sign in.
 *
 * 🔑 sessionStorage, not router state: the sign-in round-trips through
 * ZITADEL and back to /callback, so anything held in React is gone by the time
 * we return. Same store and same lifetime as the token itself.
 */
export const RETURN_TO_KEY = "ankhos.returnTo";

export function rememberReturnTo(path: string): void {
  try {
    // Only same-app paths. Storing an absolute URL here would turn the
    // post-login redirect into an open redirect.
    if (path.startsWith("/") && !path.startsWith("//")) {
      window.sessionStorage.setItem(RETURN_TO_KEY, path);
    }
  } catch {
    /* a browser refusing storage must not break sign-in */
  }
}

export function takeReturnTo(): string {
  try {
    const v = window.sessionStorage.getItem(RETURN_TO_KEY);
    window.sessionStorage.removeItem(RETURN_TO_KEY);
    return v && v.startsWith("/") && !v.startsWith("//") ? v : "/";
  } catch {
    return "/";
  }
}

/**
 * ⛔ THE GATE IS UNCHANGED IN STRENGTH. It still renders children only when
 * `auth.isAuthenticated`. What changed is only where an UNAUTHENTICATED
 * visitor is sent: to the public front door instead of straight to ZITADEL, so
 * there is something to read before being asked to log in. Adding a public
 * page must never turn into loosening this.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const location = useLocation();

  if (auth.error) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom>
          Sign-in failed
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, fontFamily: "monospace" }}>
          {auth.error.message}
        </Typography>
        <Button variant="contained" onClick={() => void auth.signinRedirect()}>
          Try again
        </Button>
      </Box>
    );
  }

  // A redirect while the session is still being restored would bounce a
  // signed-in user out to the front door on every reload.
  if (auth.isLoading || auth.activeNavigator) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 4 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading…</Typography>
      </Box>
    );
  }

  if (!auth.isAuthenticated) {
    rememberReturnTo(location.pathname + location.search);
    return <Navigate to="/welcome" replace />;
  }

  return <>{children}</>;
}
